import {
    useEffect,
    type FC,
    useId
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type ISelectionControlProps from "./type";
import type {
    SelectionControlIndicatorStateType,
    SelectionControlKeyboardEventType
} from "./type";
import stylesheet, {
    SELECTION_CONTROL_LABEL_VARIANT,
    SELECTION_CONTROL_HINT_VARIANT,
    SELECTION_CONTROL_TEXT_VARIANT,
    SELECTION_CONTROL_TOGGLE_KEY,
    getSelectionControlMeasures,
    getSelectionControlColors,
    SELECTION_CONTROL_NAMES,
    useStyles
} from "./stylesheet";
import {
    useSelectionControlRadioGroup
} from "./hook";
import {
    SELECTION_CONTROL_RADIO_DATA_KEY
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    useFieldContext
} from "../field/context";
import {
    useLabelText,
    getLabelText
} from "../label/store";
import {
    CircleAlert as CircleAlertIcon
} from "lucide-react-native";
import {
    splitControlStyle
} from "../../helpers/theme/control";
import {
    getFieldControlAccessibility
} from "../field/util";
import {
    warnOnce
} from "../../utils/development";
import {
    LabelBase
} from "../label";
import StateLayer from "../stateLayer";
import Text from "../text";

const SelectionControl: FC<ISelectionControlProps> = ({
    renderIndicator: renderIndicatorProp,
    spreadBehaviour: spreadBehaviourProp,
    "aria-labelledby": ariaLabelledBy,
    "aria-label": ariaLabel,
    accessibilityLabel,
    isDisabled = false,
    isOptional = false,
    isRequired = false,
    isLoading = false,
    variant = "plain",
    isError = false,
    customLocalize,
    isFlip = false,
    subTitleStyle,
    checkedState,
    optionalText,
    customTheme,
    onLongPress,
    onHoverOut,
    onPressOut,
    titleStyle,
    onHoverIn,
    onPressIn,
    hintText,
    subTitle,
    tabIndex,
    onFocus,
    onPress,
    onBlur,
    testID,
    style,
    title,
    role,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const spreadBehaviour = spreadBehaviourProp ?? (variant === "card" ? "free" : "baseline");

    const fieldContext = useFieldContext();

    const isFieldSettled = fieldContext?.isSettled ?? true;

    const pointerType = usePointerType();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const baseId = useId();

    const controlID = props.id ?? props.nativeID;

    const linkedLabelText = useLabelText(controlID);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onPressOut: isLoading ? undefined : onPressOut,
        onPressIn: isLoading ? undefined : onPressIn,
        isDisabled,
        onHoverOut,
        onHoverIn,
        onFocus,
        onBlur
    });

    const isWeb = Platform.OS === "web";

    const isInteractive = !!onPress || !!onLongPress;

    const isRadioManaged = isWeb && role === "radio" && isInteractive && tabIndex === undefined;

    const isErrorVisible = isError && !isDisabled;

    const isTitleVisible = !!title;

    const isCard = variant === "card";

    const isChecked = checkedState === true || checkedState === "mixed";

    const {
        accessibilityLabel: fieldAccessibilityLabel,
        accessibilityHint: fieldAccessibilityHint,
        describedBy: fieldDescribedBy,
        labelledBy: fieldLabelledBy,
        isInvalid: isFieldInvalid
    } = getFieldControlAccessibility({
        isLabelled: isTitleVisible,
        context: fieldContext
    });

    const titleId = `${baseId}-title`;

    const subTitleId = `${baseId}-sub-title`;

    const hintTextId = `${baseId}-hint-text`;

    const currentMeasures = getSelectionControlMeasures({
        controlPaddings,
        controlHeights,
        touchTargets,
        pointerType,
        typography
    });

    const currentColors = getSelectionControlColors({
        isError: isErrorVisible,
        isDisabled,
        colors
    });

    const {
        indicatorContainer: indicatorContainerDynamicStyle,
        hintTextContainer: hintTextContainerDynamicStyle,
        container: containerDynamicStyle,
        content: contentDynamicStyle,
        row: rowDynamicStyle
    } = useStyles({
        isFocusVisible: isInteractive && isFocusVisible,
        isError: isErrorVisible,
        userStyle: style,
        currentMeasures,
        spreadBehaviour,
        isTitleVisible,
        shapeConfigs,
        isDisabled,
        isChecked,
        isLoading,
        radiuses,
        borders,
        variant,
        colors,
        spaces
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const {
        handleKeyDown: handleRadioKeyDown,
        nodeRef: rowRef
    } = useSelectionControlRadioGroup({
        isManaged: isRadioManaged,
        direction
    });

    useEffect(() => {
        if(isTitleVisible || ariaLabel || accessibilityLabel || ariaLabelledBy || fieldAccessibilityLabel || fieldLabelledBy || !isFieldSettled || getLabelText(controlID)) {
            return;
        }

        warnOnce({
            message: `${SELECTION_CONTROL_NAMES[role]}: a control without a "title" needs an "accessibilityLabel" (or "aria-label") so screen readers can announce it.`
        });
    }, [
        fieldAccessibilityLabel,
        accessibilityLabel,
        linkedLabelText,
        fieldLabelledBy,
        isFieldSettled,
        ariaLabelledBy,
        isTitleVisible,
        controlID,
        ariaLabel,
        role
    ]);

    const describedBy = [
        subTitle && isTitleVisible ? subTitleId : "",
        hintText ? hintTextId : "",
        fieldDescribedBy ?? ""
    ].filter((id) => id.length > 0).join(" ");

    const hasOwnLabel = !!ariaLabel || !!accessibilityLabel || !!ariaLabelledBy;

    const ownLabelledBy = isWeb && isTitleVisible && !hasOwnLabel ? titleId : ariaLabelledBy;

    const labelledBy = ownLabelledBy ?? (isWeb && !hasOwnLabel ? fieldLabelledBy : undefined);

    const currentAccessibilityLabel = accessibilityLabel ?? (hasOwnLabel || isTitleVisible ? undefined : fieldAccessibilityLabel ?? (isWeb ? undefined : linkedLabelText));

    const currentAccessibilityHint = [
        props.accessibilityHint,
        fieldAccessibilityHint
    ].filter(Boolean).join(". ") || undefined;

    const isInvalid = isErrorVisible || isFieldInvalid;

    const indicatorState: SelectionControlIndicatorStateType = {
        isHovered: !isCard && isInteractive && !isLoading && isHovered,
        isPressed: !isCard && isInteractive && !isLoading && isPressed,
        isFocusVisible: !isCard && isInteractive && isFocusVisible
    };

    const onKeyDown = (event: SelectionControlKeyboardEventType) => {
        if(event.key !== SELECTION_CONTROL_TOGGLE_KEY) {
            handleRadioKeyDown(event);

            return;
        }

        event.preventDefault();
    };

    const onKeyUp = (event: SelectionControlKeyboardEventType) => {
        if(event.key !== SELECTION_CONTROL_TOGGLE_KEY || isDisabled || isLoading || !onPress) {
            return;
        }

        event.preventDefault();

        onPress();
    };

    const webProps = isWeb ? {
        "aria-required": role !== "radio" && isRequired ? true : undefined,
        "aria-describedby": describedBy.length ? describedBy : undefined,
        dataSet: isRadioManaged ? {
            ...interactionHandlers.dataSet,
            [SELECTION_CONTROL_RADIO_DATA_KEY]: "true"
        } : interactionHandlers.dataSet,
        "aria-invalid": isInvalid ? true : undefined,
        onKeyDown,
        onKeyUp
    } : {};

    const renderIndicatorContainer = () => {
        return <View
            style={[
                stylesheet.indicatorContainer,
                indicatorContainerDynamicStyle
            ]}
        >
            {renderIndicatorProp(indicatorState)}
        </View>;
    };

    const renderTitle = () => {
        return <LabelBase
            variant={SELECTION_CONTROL_LABEL_VARIANT}
            id={isWeb ? titleId : undefined}
            customLocalize={customLocalize}
            optionalText={optionalText}
            customTheme={customTheme}
            isDisabled={isDisabled}
            isOptional={isOptional}
            isRequired={isRequired}
            style={titleStyle}
            isRow={true}
        >
            {title}
        </LabelBase>;
    };

    const renderSubTitle = () => {
        if(!subTitle) {
            return null;
        }

        return <Text
            customColor={currentColors.subTitleColor}
            variant={SELECTION_CONTROL_TEXT_VARIANT}
            id={isWeb ? subTitleId : undefined}
            customTheme={customTheme}
            style={subTitleStyle}
        >
            {subTitle}
        </Text>;
    };

    const renderContent = () => {
        if(!isTitleVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.content,
                contentDynamicStyle
            ]}
        >
            {renderTitle()}
            {renderSubTitle()}
            {renderHintText()}
        </View>;
    };

    const renderHintIcon = () => {
        if(!isErrorVisible) {
            return null;
        }

        return <CircleAlertIcon
            color={currentColors.hintTextColor}
            size={iconSizes.small}
        />;
    };

    const renderHintText = () => {
        if(!hintText) {
            return null;
        }

        return <View
            style={[
                stylesheet.hintTextContainer,
                hintTextContainerDynamicStyle
            ]}
        >
            {renderHintIcon()}
            <Text
                customColor={currentColors.hintTextColor}
                variant={SELECTION_CONTROL_HINT_VARIANT}
                id={isWeb ? hintTextId : undefined}
                style={stylesheet.hintText}
                customTheme={customTheme}
            >
                {hintText}
            </Text>
        </View>;
    };

    const renderCardStateLayer = () => {
        if(!isCard || isDisabled) {
            return null;
        }

        return <StateLayer
            isHovered={!isLoading && isHovered}
            isPressed={!isLoading && isPressed}
            borderRadius={radiuses.actions}
            borderWidth={borders.line}
            customTheme={customTheme}
            isSquircle={true}
        />;
    };

    const renderRow = () => {
        if(!isInteractive) {
            return <View
                accessibilityLabel={currentAccessibilityLabel}
                aria-checked={checkedState}
                aria-disabled={isDisabled}
                aria-label={ariaLabel}
                style={[
                    surfaceStyle,
                    stylesheet.row,
                    rowDynamicStyle
                ]}
                focusable={false}
                testID={testID}
                role={role}
            >
                {isFlip ? null : renderIndicatorContainer()}
                {renderContent()}
                {isFlip ? renderIndicatorContainer() : null}
            </View>;
        }

        return <Pressable
            {...props}
            {...interactionHandlers}
            {...webProps}
            onLongPress={isLoading ? undefined : onLongPress}
            accessibilityLabel={currentAccessibilityLabel}
            accessibilityHint={currentAccessibilityHint}
            onPress={isLoading ? undefined : onPress}
            aria-labelledby={labelledBy}
            aria-checked={checkedState}
            aria-disabled={isDisabled}
            aria-label={ariaLabel}
            aria-busy={isLoading}
            disabled={isDisabled}
            style={[
                surfaceStyle,
                stylesheet.row,
                rowDynamicStyle
            ]}
            tabIndex={tabIndex}
            testID={testID}
            ref={rowRef}
            role={role}
        >
            {renderCardStateLayer()}
            {isFlip ? null : renderIndicatorContainer()}
            {renderContent()}
            {isFlip ? renderIndicatorContainer() : null}
        </Pressable>;
    };

    return <View
        style={[
            containerStyle,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {renderRow()}
        {isTitleVisible ? null : renderHintText()}
    </View>;
};
export default SelectionControl;
